/**
 * FailedBanner — red banner shown when a Job ends `status='failed'`.
 *
 * Renders Aria's failure narration (or a generic fallback) plus the three
 * remediation paths from docs/product/job-detail.md §6.5:
 *   - Retry as-is        → /jobs/new?template=X&runAgainOf=Y  (template-derived)
 *   - Edit inputs & retry → /jobs/new?template=X&fromJob=Y    (template-derived)
 *   - Abandon            → callback (typically navigates to /jobs)
 *
 * For ad-hoc Jobs (no created_from_template_id) only Abandon is offered —
 * spawning a fresh ad-hoc Job is filed as a future flow.
 */
import { Link } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { t } from '@/lib/i18n';
import styles from './FailedBanner.module.css';

export interface FailedBannerProps {
  errorMessage: string | null;
  jobId: string;
  templateId: string | null;
  memberName: string;
  onAbandon: () => void;
}

export function FailedBanner({
  errorMessage,
  jobId,
  templateId,
  memberName,
  onAbandon,
}: FailedBannerProps) {
  const title = t("{name} couldn't finish this Job", { name: memberName });
  const body = errorMessage ?? t('{name} hit an unexpected problem.', { name: memberName });
  const canRetry = templateId !== null;

  return (
    <div className={styles.banner} role="alert">
      <div className={styles.bannerHeader}>
        <span className={styles.bannerIcon} aria-hidden="true">
          ❌
        </span>
        <span className={styles.bannerTitle}>{title}</span>
      </div>
      <p className={styles.body}>{body}</p>
      <div className={styles.actions}>
        {canRetry && templateId ? (
          <>
            <Link
              to={`/jobs/new?template=${encodeURIComponent(templateId)}&runAgainOf=${encodeURIComponent(jobId)}`}
              className={styles.actionLink}
            >
              <Button variant="primary">{t('Retry as-is')}</Button>
            </Link>
            <Link
              to={`/jobs/new?template=${encodeURIComponent(templateId)}&fromJob=${encodeURIComponent(jobId)}`}
              className={styles.actionLink}
            >
              <Button variant="secondary">{t('Edit inputs & retry')}</Button>
            </Link>
          </>
        ) : null}
        <Button variant="ghost" onClick={onAbandon}>
          {t('Abandon')}
        </Button>
      </div>
    </div>
  );
}
